import appConfig from "@/configs/app-config";
import { TrackingEvent } from "../../constants/events";
import { pushToDataLayer } from "../../providers/google/utils";
import {
  buildAddPaymentInfoEcommerce,
  buildBeginCheckoutEcommerce,
  buildBusEcommerceItem,
  buildExtraServiceEcommerce,
  buildPurchaseEcommerce,
  buildRefundEcommerce,
} from "./utils";

/** Boş string yerine null döndür - dataLayer'a "" gönderilmemeli */
const emptyToNull = (v) =>
  v === "" || v == null || v === undefined ? null : v;

/**
 * Google Analytics - Otobüs Provider
 * Otobüs kategorisi için Google Analytics event'lerini yönetir
 */
export class BusGoogleProvider {
  track(event, data) {
    switch (event) {
      case TrackingEvent.VIRTUAL_PV:
        this.trackVirtualPv(data);
        break;
      case TrackingEvent.SEARCH_RESULT:
        this.trackSearchResult(data);
        break;
      case TrackingEvent.NO_SEARCH_RESULT:
        this.trackNoSearchResult(data);
        break;
      case TrackingEvent.CHECK_RESERVATION:
        this.trackCheckReservation(data);
        break;
      case TrackingEvent.VIEW_ITEM_LIST:
        this.trackViewItemList(data);
        break;
      case TrackingEvent.SELECT_ITEM:
        this.trackSelectItem(data);
        break;
      case TrackingEvent.VIEW_ITEM:
        this.trackViewItem(data);
        break;
      case TrackingEvent.ADD_TO_CART:
        this.trackAddToCart(data);
        break;
      case TrackingEvent.BEGIN_CHECKOUT:
        this.trackBeginCheckout(data);
        break;
      case TrackingEvent.EXTRA_SERVICE:
        this.trackExtraService(data);
        break;
      case TrackingEvent.ADD_PAYMENT_INFO:
        this.trackAddPaymentInfo(data);
        break;
      case TrackingEvent.PURCHASE:
        this.trackPurchase(data);
        break;
      case TrackingEvent.REFUND:
        this.trackRefund(data);
        break;
      case TrackingEvent.FILTER_APPLIED:
        this.trackFilterApplied(data);
        break;
      case TrackingEvent.SORTING_APPLIED:
        this.trackSortingApplied(data);
        break;
      case TrackingEvent.PROMOTIONAL_BANNER_CLICKED:
        this.trackPromotionalBannerClicked(data);
        break;
      case TrackingEvent.CTA_CLICKED:
        this.trackCtaClicked(data);
        break;
      case TrackingEvent.SELECT_COUPON:
      case TrackingEvent.VIEW_COUPON:
      case TrackingEvent.COUPON_APPLIED:
        this.trackCouponEvent(event, data);
        break;
      default:
        this.trackGenericEvent(event, data);
    }
  }

  trackVirtualPv(data) {
    pushToDataLayer(TrackingEvent.VIRTUAL_PV, {
      event: "virtual_pv",
      environment: data.environment || "prod",
      virtual_page_url: emptyToNull(data.virtual_page_url),
      page_type: emptyToNull(data.page_type),
      bus_destination: emptyToNull(data.bus_destination),
      bus_category: emptyToNull(data.bus_category),
      bus_company: emptyToNull(data.bus_company),
      bus_arrival: emptyToNull(data.bus_arrival),
      bus_departure: emptyToNull(data.bus_departure),
      bus_terminal: emptyToNull(data.bus_terminal),
      language: emptyToNull(data.language),
      turna_session_id: emptyToNull(data.turna_session_id),
      turna_device_id: emptyToNull(data.turna_device_id),
      version: appConfig.WEBSITE_VERSION,
      digital_platform_country: appConfig.COUNTRY_CODE,
      user_id: data.user_id.toString() ?? "",
      user_hashed_email: emptyToNull(data.user_hashed_email),
      user_hashed_phone: emptyToNull(data.user_hashed_phone),
      user_type: data.user_type ?? "guest",
      guest_user_hashed_email: emptyToNull(data.guest_user_hashed_email),
      guest_user_hashed_phone: emptyToNull(data.guest_user_hashed_phone),
      // user_mail: emptyToNull(data.user_mail),
      // user_phone: emptyToNull(data.user_phone),
      user_turna_points: emptyToNull(data.user_turna_points),
      first_time_purchaser: emptyToNull(data.first_time_purchaser),
    });
  }

  trackSearchResult(data) {
    pushToDataLayer(TrackingEvent.SEARCH_RESULT, {
      event: "custom_event",
      event_category: "search",
      event_action: "search_result",
      search_category: "bus",
      search_destination: emptyToNull(data.search_destination),
      start_date: data.start_date ?? null,
      passengers_total: data.passengers_total ?? null,
      search_result_number: emptyToNull(data.search_result_number),
    });
  }

  trackNoSearchResult(data) {
    pushToDataLayer(TrackingEvent.NO_SEARCH_RESULT, {
      event: "custom_event",
      event_category: "search",
      event_action: "no_search_result",
      search_category: "bus",
      search_term: emptyToNull(data.search_term),
      search_destination: emptyToNull(data.search_destination),
      start_date: data.start_date ?? null,
      passengers_total: data.passengers_total ?? null,
    });
  }

  trackCheckReservation(data) {
    pushToDataLayer(TrackingEvent.CHECK_RESERVATION, {
      event: "custom_event",
      event_category: "reservation",
      event_action: "check_reservation",
      pnr_number: emptyToNull(data.pnr_number),
      reservation_category: "bus",
    });
  }

  trackViewItemList(data) {
    let items = [];

    if (Array.isArray(data?.legs) && data.legs.length > 0) {
      for (const { leg, index } of data.legs) {
        const legItems = buildBusEcommerceItem({
          leg,
          index,
          summaryData: data?.summaryData,
          item_list_id: "Search_List",
          item_list_name: "Search List",
        });
        items = items.concat(legItems);
      }
    } else {
      items = buildBusEcommerceItem({
        leg: data?.leg,
        index: data?.index,
        summaryData: data?.summaryData,
        item_list_id: "Search_List",
        item_list_name: "Search List",
      });
    }

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.VIEW_ITEM_LIST, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "view_item_list",
      ecommerce: {
        item_list_id: "Search_List",
        item_list_name: "Search List",
        items,
      },
    });
  }

  trackSelectItem(data) {
    const items = buildBusEcommerceItem({
      leg: data?.leg,
      index: data?.index ?? 0,
      summaryData: data?.summaryData,
      item_list_id: "Search_List",
      item_list_name: "Search List",
    });

    const ecommerce = {
      item_list_id: "Search_List",
      item_list_name: "Search List",
      items,
      ...(data?.basketId != null && String(data.basketId).trim() !== ""
        ? { basket_id: String(data.basketId) }
        : {}),
    };

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.SELECT_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "select_item",
      ecommerce,
    });
  }

  trackViewItem(data) {
    const ecommerce =
      data?.ecommerce ??
      (() => {
        const items = buildBusEcommerceItem({
          leg: data?.leg,
          index: data?.index ?? 0,
          summaryData: data?.summaryData,
          item_list_id: "Search_List",
          item_list_name: "Search List",
        });
        return {
          currency: data?.currencyCode,
          value: data?.leg?.PaxPricesSummary?.DiscountedTotal || 0,
          items,
        };
      })();

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.VIEW_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "view_item",
      ecommerce,
    });
  }

  trackAddToCart(data) {
    const quantity = data?.allocatePayload?.SelectedSeats?.length || 1;

    const ecommerce =
      data?.ecommerce ??
      (() => {
        const items = buildBusEcommerceItem({
          leg: data?.leg,
          index: data?.index ?? 0,
          summaryData: data?.summaryData,
          item_list_id: "Search_List",
          item_list_name: "Search List",
          quantity: quantity || 1,
        });
        return {
          currency: data?.currencyCode,
          value:
            (data?.fareResult?.TotalFare || 0) +
            (data?.fareResult?.AdditionalFare || 0),
          items,
          ...(data?.basketId != null && String(data.basketId).trim() !== ""
            ? { basket_id: String(data.basketId) }
            : {}),
        };
      })();

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_TO_CART, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_to_cart",
      ecommerce,
    });
  }

  trackBeginCheckout(data) {
    const ecommerce =
      data.ecommerce ??
      buildBeginCheckoutEcommerce({
        products: data.products,
        totalValue: data.totalValue,
        currency: data.currency,
        basketId: data.basketId,
        basketData: data.basketData,
      });

    if (!ecommerce) return;

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.BEGIN_CHECKOUT, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "begin_checkout",
      ecommerce,
    });
  }

  /**
   * Extra service (sigorta vb.) add_to_cart / remove_from_cart
   * event_category: "extra_service"
   */
  trackExtraService(data) {
    const resolved =
      data.ecommerce != null && data.eventAction != null
        ? { eventAction: data.eventAction, ecommerce: data.ecommerce }
        : buildExtraServiceEcommerce({
            isSelected: data.isSelected,
            insurancePrice: data.insurancePrice,
            currency: data.currency,
            basketId: data.basketId,
          });

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer("ecom_event", {
      event_category: "extra_service",
      event_action: resolved.eventAction || "add_to_cart",
      ecommerce: resolved.ecommerce,
    });
  }

  trackAddPaymentInfo(data) {
    const ecommerce =
      data.ecommerce ??
      buildAddPaymentInfoEcommerce({
        paymentInfo: data.paymentInfo,
        selectedCoponCode: data.selectedCoponCode,
        selectedInstalment: data.selectedInstalment,
        currency: data.currency,
      });

    if (!ecommerce) return;

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_PAYMENT_INFO, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_payment_info",
      ecommerce,
    });
  }

  trackPurchase(data) {
    const ecommerce =
      data.ecommerce ??
      buildPurchaseEcommerce({
        paymentInfo: data.paymentInfo,
        cardProgram: data.cardProgram,
        currency: data.currency,
        selectedCoponCode: data.selectedCoponCode,
        creditCardPayment: data.creditCardPayment,
        bankInfo: data.bankInfo,
      });

    if (!ecommerce) return;

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.PURCHASE, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "purchase",
      ecommerce,
    });
  }

  trackRefund(data) {
    const ecommerce =
      data.ecommerce ??
      buildRefundEcommerce({
        products: data.products,
        basketData: data.basketData,
        priceSummary: data.priceSummary,
        cancelQuantity: data.cancelQuantity ?? 1,
        currency: data.currency,
      });

    if (!ecommerce) return;

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.REFUND, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "refund",
      ecommerce,
    });
  }

  trackFilterApplied(data) {
    pushToDataLayer(TrackingEvent.FILTER_APPLIED, {
      event: "custom_event",
      event_category: "filtering",
      event_action: "filter_applied",
      filter_product_category: "bus",
      filter_category: emptyToNull(data?.filterName),
      filter_subcategory: emptyToNull(data?.label),
      sorting_type: emptyToNull(
        `${data?.sort?.SortBy}_${data?.sort?.Ascending ? "asc" : "desc"}`,
      ),
    });
  }

  trackSortingApplied(data) {
    pushToDataLayer(TrackingEvent.SORTING_APPLIED, {
      event: "custom_event",
      event_category: "filtering",
      event_action: "sorting_applied",
      filter_product_category: "bus",
      filter_category: "sorting",
      filter_subcategory: emptyToNull(
        `${data?.sortBy}_${data?.ascending ? "asc" : "desc"}`,
      ),
    });
  }

  trackPromotionalBannerClicked(data) {
    pushToDataLayer(TrackingEvent.PROMOTIONAL_BANNER_CLICKED, {
      event: "custom_event",
      event_category: "switch",
      event_action: emptyToNull(`switch_to_${data?.type}`),
      [`${data?.type}_route`]: emptyToNull(
        `${data?.originCode}-${data?.destinationCode}`,
      ),
      bus_route: emptyToNull(`${data?.fromCity}-${data?.toCity}`),
    });
  }

  /**
   * CTA tıklaması - analyticsPayload ile gelen dinamik veriler kullanılır
   * cta_button, cta_location, cta_category, cta_language analyticsPayload'dan gelir
   */
  trackCtaClicked(data) {
    pushToDataLayer(TrackingEvent.CTA_CLICKED, {
      event: "custom_event",
      event_category: "click",
      event_action: "cta_click",
      cta_button: emptyToNull(data.cta_button),
      cta_location:
        data.cta_location === "" ? null : (data.cta_location ?? "body"),
      cta_category: emptyToNull(data.cta_category),
      cta_language:
        data.cta_language === "" ? null : (data.cta_language ?? "TR"),
    });
  }

  trackCouponEvent(event, data) {
    // Extract coupon data from raw offer object
    const offer = data.offer || {};
    const currencyCode = data.currencyCode || "TRY";

    const couponName = emptyToNull(offer?.code);
    const couponValue = emptyToNull(offer?.discountText);

    pushToDataLayer(event, {
      event: "custom_event",
      event_category: "coupon",
      event_action: event,
      coupon_name: couponName,
      coupon_value: null,
      coupon_currency: currencyCode,
      coupon_category: "bus",
    });
  }

  trackGenericEvent(event, data) {
    pushToDataLayer(event, data);
  }
}
